import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { Building2, ChevronRight, Sparkles } from "lucide-react";

export default function RegisterWizardPage() {
  const { register, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0); // 0 account, 1 community, 2 flats, 3 done
  const [busy, setBusy] = useState(false);

  // step 0
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");

  // step 1
  const [cName, setCName] = useState("");
  const [cAddr, setCAddr] = useState("");
  const [cCity, setCCity] = useState("");

  // step 2
  const [pattern, setPattern] = useState("A-101..A-204"); // friendly pattern
  const [flatsList, setFlatsList] = useState("");

  function expandPattern(p) {
    // Accept pattern like "A-101..A-204" or comma list
    const out = new Set();
    const segs = p.split(/[,\n]/).map((x) => x.trim()).filter(Boolean);
    for (const s of segs) {
      const m = s.match(/^([A-Za-z]?-?)?(\d+)\.\.([A-Za-z]?-?)?(\d+)$/);
      if (m) {
        const prefix = m[1] || "";
        const start = parseInt(m[2], 10);
        const end = parseInt(m[4], 10);
        for (let i = start; i <= end; i++) out.add(`${prefix}${i}`);
      } else {
        out.add(s);
      }
    }
    return Array.from(out);
  }

  async function submitAccount(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await register({ name, email, password, phone });
      setStep(1);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  }

  async function submitCommunity(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/communities", { name: cName, address: cAddr, city: cCity });
      await refresh();
      setStep(2);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  }

  async function submitFlats(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const me = await refresh();
      const labels = expandPattern(flatsList || pattern);
      await api.post(`/communities/${me.community_id}/flats/bulk`, { labels });
      setStep(3);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  }

  const progress = ((step + 1) / 4) * 100;
  const previewFlats = expandPattern(flatsList || pattern).slice(0, 12);

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      <div className="px-6 py-5 border-b border-stone-200 bg-white flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-[#C05640] text-white grid place-items-center">
          <Building2 size={16} />
        </div>
        <div>
          <div className="font-serif-display font-semibold leading-none">NivasOne</div>
          <div className="text-[10px] text-stone-500 tracking-wide">Set up your apartment</div>
        </div>
        <div className="ml-auto w-40">
          <Progress value={progress} className="h-1.5" />
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-5 md:p-10">
        <div className="w-full max-w-xl">
          {step === 0 && (
            <form onSubmit={submitAccount} className="bg-white rounded-3xl border border-stone-200 p-6 md:p-8" data-testid="step-account">
              <h2 className="font-serif-display text-3xl font-semibold">Hi, champion 👋</h2>
              <p className="text-sm text-stone-500 mt-1">Let's start with your account. We'll build your apartment in the next steps.</p>
              <div className="grid gap-4 mt-6">
                <div><Label>Your name</Label><Input value={name} required onChange={(e)=>setName(e.target.value)} data-testid="reg-name" className="h-12 mt-1.5" /></div>
                <div><Label>Email</Label><Input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="reg-email" className="h-12 mt-1.5" /></div>
                <div><Label>Password</Label><Input type="password" required value={password} onChange={(e)=>setPassword(e.target.value)} data-testid="reg-password" className="h-12 mt-1.5" /></div>
                <div><Label>Phone (optional)</Label><Input value={phone} onChange={(e)=>setPhone(e.target.value)} data-testid="reg-phone" className="h-12 mt-1.5" placeholder="+91-…" /></div>
              </div>
              <Button type="submit" disabled={busy} data-testid="reg-next-1"
                className="w-full h-12 mt-6 rounded-full bg-[#C05640] hover:bg-[#A54734]">
                {busy ? "…" : "Continue"} <ChevronRight size={18} />
              </Button>
              <p className="text-sm text-stone-500 mt-4 text-center">
                Already onboarded? <Link to="/login" className="text-[#C05640] font-semibold">Sign in</Link>
              </p>
            </form>
          )}

          {step === 1 && (
            <form onSubmit={submitCommunity} className="bg-white rounded-3xl border border-stone-200 p-6 md:p-8" data-testid="step-community">
              <h2 className="font-serif-display text-3xl font-semibold">Your building</h2>
              <p className="text-sm text-stone-500 mt-1">Just enough to get started — you can edit anything later.</p>
              <div className="grid gap-4 mt-6">
                <div><Label>Apartment / community name</Label><Input value={cName} required onChange={(e)=>setCName(e.target.value)} data-testid="comm-name" className="h-12 mt-1.5" placeholder="e.g. Sundar Apartments" /></div>
                <div><Label>Address</Label><Textarea value={cAddr} required onChange={(e)=>setCAddr(e.target.value)} data-testid="comm-addr" className="mt-1.5" rows={2} /></div>
                <div><Label>City</Label><Input value={cCity} onChange={(e)=>setCCity(e.target.value)} data-testid="comm-city" className="h-12 mt-1.5" placeholder="Bengaluru" /></div>
              </div>
              <Button type="submit" disabled={busy} data-testid="reg-next-2"
                className="w-full h-12 mt-6 rounded-full bg-[#C05640] hover:bg-[#A54734]">
                Continue <ChevronRight size={18} />
              </Button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={submitFlats} className="bg-white rounded-3xl border border-stone-200 p-6 md:p-8" data-testid="step-flats">
              <h2 className="font-serif-display text-3xl font-semibold">Add flats</h2>
              <p className="text-sm text-stone-500 mt-1">Type a quick pattern (e.g. <code className="bg-stone-100 px-1.5 py-0.5 rounded">A-101..A-204</code>) or paste a comma-separated list. You can also skip and add later.</p>
              <Textarea rows={5} value={flatsList} onChange={(e)=>setFlatsList(e.target.value)}
                placeholder="A-101..A-204\nB-101..B-204"
                className="mt-4 font-mono text-sm"
                data-testid="flats-input" />
              <div className="mt-3 flex flex-wrap gap-1.5">
                {previewFlats.map((f) => (
                  <span key={f} className="px-2 py-1 rounded-md bg-[#F4F1EA] text-stone-700 text-xs font-medium">{f}</span>
                ))}
                {expandPattern(flatsList || pattern).length > 12 && (
                  <span className="text-xs text-stone-500">+{expandPattern(flatsList || pattern).length - 12} more</span>
                )}
              </div>
              <div className="flex gap-3 mt-6">
                <Button type="button" variant="ghost" onClick={() => setStep(3)} data-testid="skip-flats" className="rounded-full">Skip</Button>
                <Button type="submit" disabled={busy} data-testid="create-flats"
                  className="flex-1 h-12 rounded-full bg-[#C05640] hover:bg-[#A54734]">
                  {busy ? "…" : "Create flats"} <ChevronRight size={18} />
                </Button>
              </div>
            </form>
          )}

          {step === 3 && (
            <div className="bg-white rounded-3xl border border-stone-200 p-8 text-center" data-testid="step-done">
              <div className="w-14 h-14 rounded-full bg-[#E8D8D3] grid place-items-center mx-auto">
                <Sparkles size={26} className="text-[#C05640]" />
              </div>
              <h2 className="font-serif-display text-3xl font-semibold mt-4">You're all set</h2>
              <p className="text-stone-600 mt-2">Your apartment workspace is live. Invite committee, raise your first issue, or jot down a meeting.</p>
              <div className="grid grid-cols-2 gap-3 mt-6">
                <Button onClick={() => nav("/directory")} variant="outline" className="rounded-full h-12" data-testid="goto-directory">Invite people</Button>
                <Button onClick={() => nav("/dashboard")} className="rounded-full h-12 bg-[#C05640] hover:bg-[#A54734]" data-testid="goto-dashboard">Open dashboard</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
